import { nil, NO, UIColor, UITextView } from "uicore-ts"
import { RHContractingAuthorityRules } from "../../ContractingAuthorityRules"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHPanelView } from "../../Custom components/RH/RHPanelView"
import { RHButtons, RHColors, RHFont, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import type { RHContractingAuthorityType } from "../../SocketClientFunctions"
import { CompanyContractingAuthorityOptionView } from "./CompanyContractingAuthorityOptionView"


export class CompanyContractingAuthoritySettingsView extends RHPanelView {

    readonly titleLabel = new UITextView()
        .configuredWithObject({
            text: "Hankija määratlus",
            textColor: RHColors.textHeading,
            style: { fontSize: RHFont.sizeLarge, fontWeight: RHFont.weightSemibold }
        })
        .addedAsSubviewToView(this)

    readonly explanationLabel = new UITextView()
        .configuredWithObject({
            text: "Vali üks või mitu määratlust. Muudatus rakendub uutele hangetele.",
            textColor: RHColors.textSecondary,
            isSingleLine: NO
        })
        .addedAsSubviewToView(this)

    readonly options = RHContractingAuthorityRules.types.map(authorityType => {
        const option = new CompanyContractingAuthorityOptionView(authorityType)
            .addedAsSubviewToView(this)
        option.checkbox.controlEventTargetAccumulator.SelectionChange = () => this._updateSaveAvailability()
        return option
    })

    readonly thresholdNoticeLabel = new UITextView()
        .configuredWithObject({
            text: RHContractingAuthorityRules.thresholdNotice,
            textColor: RHColors.textSecondary,
            isSingleLine: NO,
            style: { fontSize: RHFont.sizeSmall }
        })
        .addedAsSubviewToView(this)

    readonly saveButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Salvesta", style: { fontSize: RHButtons.compactPrimaryFontSize } },
            variant: "success",
            contentPadding: RHButtons.compactPrimaryContentPadding,
            style: { fontSize: RHButtons.compactPrimaryFontSize },
            enabled: NO
        })
        .addedAsSubviewToView(this)

    override _focusManager = new RHFocusManager(() => [
        ...this.options.map(option => option.checkbox),
        this.saveButton
    ])

    _savedTypes: RHContractingAuthorityType[] = []
    _isSaving = NO


    constructor(elementID?: string) {
        super(elementID)
        ;(this as CompanyContractingAuthoritySettingsView).configureWithObject({ backgroundColor: UIColor.whiteColor })
    }


    get selectedTypes(): RHContractingAuthorityType[] {
        return this.options.filter(option => option.checkbox.selected).map(option => option.authorityType)
    }


    get hasUnsavedChanges(): boolean {
        return JSON.stringify(this.selectedTypes) !== JSON.stringify(this._savedTypes)
    }


    set isSaving(isSaving: boolean) {
        this._isSaving = isSaving
        this.options.forEach(option => { option.checkbox.enabled = !isSaving })
        this._updateSaveAvailability()
    }


    applySavedTypes(authorityTypes: RHContractingAuthorityType[]): void {
        this._savedTypes = RHContractingAuthorityRules.types.filter(authorityType => authorityTypes.includes(authorityType))
        this.options.forEach(option => { option.checkbox.selected = this._savedTypes.includes(option.authorityType) })
        this._updateSaveAvailability()
    }


    _updateSaveAvailability(): void {
        this.saveButton.enabled = !this._isSaving && this.selectedTypes.length > 0 && this.hasUnsavedChanges
    }


    override layoutSubviews() {
        super.layoutSubviews()
        const bounds = this.contentBoundsWithInset(RHSpacing.medium)
        const saveButtonWidth = Math.min(bounds.width, Math.ceil(
            this.saveButton.titleLabel.intrinsicContentWidth(RHButtons.compactPrimaryHeight) +
            this.saveButton.contentPadding * 2 +
            parseFloat(this.saveButton.style.borderLeftWidth) + parseFloat(this.saveButton.style.borderRightWidth)
        ))
        const footerFitsOnOneRow = this.thresholdNoticeLabel.intrinsicContentWidth() +
            RHSpacing.medium + saveButtonWidth <= bounds.width
        bounds
            .rectangleWithHeight(this.titleLabel)
            .assignedAsFrameOfView(this.titleLabel)
            .rectangleForNextRow(RHSpacing.small, this.explanationLabel)
            .assignedAsFrameOfView(this.explanationLabel)
            .rectangleForNextRow(RHSpacing.medium, 0)
            .framesByDistributingViewsAsColumn(
                this.options,
                RHSpacing.small
            )
            .boundingBox()
            .rectangleForNextRow(RHSpacing.medium, 0)
            .IF(footerFitsOnOneRow)
            .rectangleWithHeight(RHButtons.compactPrimaryHeight)
            .distributeViewsAlongWidth(
                [this.thresholdNoticeLabel, this.saveButton],
                [1, nil], RHSpacing.medium, [nil, saveButtonWidth]
            )
            .ELSE()
            .rectangleWithHeight(this.thresholdNoticeLabel)
            .assignedAsFrameOfView(this.thresholdNoticeLabel)
            .rectangleForNextRow(RHSpacing.small, RHButtons.compactPrimaryHeight)
            .rectangleWithWidth(saveButtonWidth, 1)
            .assignedAsFrameOfView(this.saveButton)
            .ENDIF()
    }
}
